import React, { FC, useEffect, useCallback, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate, useMatch, Navigate } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { get, isEqual } from 'lodash';

import { fetchQmr, useQmrDispatch, useQmrState, useQmrInfoRequests } from '@packages/contexts/qmrs';
import { useMarkNotificationAsReadOnLoad } from '@packages/contexts/notifications';
import { useAbortController } from '@packages/core/http';
import { FailCode, QmrStatusId, TrReplyStatusType } from '@packages/models/api';
import { AlertBar, Icon, Typography } from '@packages/ui/shared';
import ToastNotification from '@web/components/toast-notification';

import { failCodesService, notificationsService, qmrsService } from '@web/services/singletons';

import QmrLayout from '@web/qmr/components/qmr-layout';
import VehicleInformationDraft from '@web/qmr/components/vehicle-information-draft';
import VehicleInformationSubmitted from '@web/qmr/components/vehicle-information-submitted';
import Attachments from '@web/qmr/components/attachments';
import ConcernDraft from '@web/qmr/components/concern-draft';
import ConcernSubmitted from '@web/qmr/components/concern-submitted';
import CauseDraft from '@web/qmr/components/cause-draft';
import CauseSubmitted from '@web/qmr/components/cause-submitted';
import CorrectionDraft from '@web/qmr/components/correction-draft';
import CorrectionSubmitted from '@web/qmr/components/correction-submitted';
import DtcCodesSubmitted from '@web/qmr/components/dtc-codes-submitted';
import DtcCodesDraft from '@web/qmr/components/dtc-codes-draft';
import FailCodeReportSection from '@web/qmr/components/fail-code-report-section';
import { QmrFeedbackDrawer } from '../components/qmr-feedback-drawer';
import { AdditionalInformation } from '@web/qmr/components/additional-information';
import { scrollIntoView } from '@web/utils';
import Loader from '@web/components/loader';
import { QmrNotFound } from '@web/qmr/components/qmr-not-found';
import colors from '@packages/core/styles/colors';
import NoMatch from '@web/views/no-match';
import QmrOutcome from '../components/qmr-outcome';
import { useAuthState } from '@packages/contexts/auth';
import PrimaryObjective from '@web/qmr/components/primary-objective';
import { isPrimaryObjectiveEnabled } from '@web/config/constants';

const useQmrDetailStyles = createUseStyles({
    section: {
        padding: `0px 40px`,
    },
    sectionDivider: {
        margin: '24px 40px',
    },
    infoSection: {
        paddingTop: 24,
        paddingBottom: 24,
        backgroundColor: colors.grayOne,
        borderTop: `1px solid ${colors.grayThree}`,
    },
});

const sectionIdsMap = {
    vehicle: 'vehicle-info',
    primaryObjective: 'primary-objective',
    concern: 'concern',
    cause: 'cause',
    correction: 'correction',
    dtc: 'dtc',
    failCodes: 'fail-codes',
    attachments: 'attachments',
    additionalInfo: 'additional-info',
    qmrOutcome: 'qmr-outcome',
};

const CHECKLIST_CONTAINER_ID = 'report-details';
const CHECKLIST_SECTION_IDS = Object.values(sectionIdsMap);

const QmrDetail: FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { displayIdentifier } = useParams<{ displayIdentifier: string }>();
    const qmrDispatch = useQmrDispatch();
    const { abortSignalRef } = useAbortController();
    const { qmr, isQmrFeedbackDrawerVisible } = useQmrState();
    const classes = useQmrDetailStyles();
    const editRouteMatch = useMatch(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}/:displayIdentifier/edit`);
    const { hasOpenInfoRequest } = useQmrInfoRequests({ qmrsService });

    const [isFetching, setIsFetching] = useState(false);
    const [qmrFetchRestricted, setQmrFetchRestricted] = useState(false);
    const [showRecentlyCreatedMessage, setShowRecentlyCreatedMessage] = useState(false);
    const [restrictedRecentlyState, setRestrictedRecentlyState] = useState({
        restricted: false,
        unrestricted: false,
    });
    const [matchingFailCode, setMatchingFailCode] = useState<FailCode | null>(null);
    const [showQmrEscalatedTrMessage, setShowQmrEscalatedTrMessage] = useState(false);
    const [showQmrEscalatedFailedMessage, setShowQmrEscalatedFailedMessage] = useState(false);
    const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const { account } = useAuthState();
    const [toggle, setToggle] = useState<boolean>(false);
    const [qmrTypeChangedMsg, setQmrTypeChangedMsg] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const hasPageRendered = useRef(false);

    const isSubmitted = useMemo(
        () =>
            !!qmr?.qmrStatus?.qmrStatusId &&
            qmr.qmrStatus.qmrStatusId !== QmrStatusId.Draft &&
            qmr?.createdAccount?.accountId === account?.accountId,
        [qmr, account]
    );
    const isDraft = qmr?.qmrStatus.qmrStatusId === QmrStatusId.Draft;
    const isEditing = isDraft || !!editRouteMatch;
    const refetchDetails = !qmr || isEditing;

    const handleShowToast = useCallback((message: string) => {
        setToast({ show: true, message });
    }, []);

    const handleCloseToast = useCallback(() => {
        setToast((currentToast) => ({ ...currentToast, show: false }));
    }, []);

    useEffect(() => {
        if (qmr) {
            if (qmr?.isNqr && location.pathname.includes('/qmrs/')) {
                if (!isInitialLoad) {
                    setTimeout(() => {
                        navigate(
                            {
                                pathname: location.pathname.replace('/qmrs/', '/nqrs/'),
                                search: location.search,
                            },
                            {
                                state: {
                                    message: 'The report has been successfully updated and changed to NQR.',
                                },
                            }
                        );
                    });
                } else {
                    navigate({
                        pathname: location.pathname.replace('/qmrs/', '/nqrs/'),
                        search: location.search,
                    });
                }
            } else if (!qmr?.isNqr && location.pathname.includes('/nqrs/')) {
                if (!isInitialLoad) {
                    setTimeout(() => {
                        navigate(
                            {
                                pathname: location.pathname.replace('/nqrs/', '/qmrs/'),
                                search: location.search,
                            },
                            {
                                state: {
                                    message: 'The report has been successfully updated and changed to QMR.',
                                },
                            }
                        );
                    });
                } else {
                    navigate({
                        pathname: location.pathname.replace('/nqrs/', '/qmrs/'),
                        search: location.search,
                    });
                }
            }
            setIsInitialLoad(false);
        }
    }, [isInitialLoad, location.pathname, location.search, navigate, qmr, qmr?.isNqr]);

    useMarkNotificationAsReadOnLoad(notificationsService);

    const fetchQmrDetail = useCallback(
        (clearLocalPatch: boolean) => {
            if (!displayIdentifier) {
                return;
            }

            setIsFetching(true);

            fetchQmr({
                clearLocalPatch,
                qmrId: displayIdentifier,
                qmrsService,
                qmrDispatch,
                signal: abortSignalRef.current,
                ignoreCache: true,
            })
                .then(() => {
                    setIsFetching(false);
                })
                .catch((e) => {
                    setIsFetching(false);

                    if (e.code === 'AUTHORIZATION_REQUIRED') {
                        setQmrFetchRestricted(true);
                        return;
                    }
                });

            setToggle(false);
        },
        [displayIdentifier, qmrDispatch, abortSignalRef]
    );

    useLayoutEffect(() => {
        const hashFragment = (location.hash || '').replace('#', '');

        if (hashFragment !== '') {
            scrollIntoView(hashFragment);
        }
    }, [location.hash]);

    useEffect(
        () => {
            if (refetchDetails && hasPageRendered.current) {
                fetchQmrDetail(true);
            }
            hasPageRendered.current = true;
        },
        // re-fetch qmr when toggling between edit states
        [refetchDetails, fetchQmrDetail]
    );

    // Fetch details on first load only
    useEffect(() => {
        fetchQmrDetail(true);
    }, [fetchQmrDetail]);

    useEffect(() => {
        if (location.state && location.state?.isRecent) {
            setShowRecentlyCreatedMessage(true);
        }
    }, [location.state]);

    useEffect(() => {
        const trReplyStatusType = get(qmr, 'trReplyStatus.trReplyStatusId');
        if (get(qmr, 'capabilities.canViewEscalationOnQmr', false)) {
            if (trReplyStatusType && isEqual(trReplyStatusType, TrReplyStatusType.EscalationFailed)) {
                setShowQmrEscalatedFailedMessage(true);
                setShowQmrEscalatedTrMessage(false);
            } else if (!isEqual(trReplyStatusType, TrReplyStatusType.NotEscalated)) {
                setShowQmrEscalatedTrMessage(true);
                setShowQmrEscalatedFailedMessage(false);
            }
        }
    }, [qmr]);

    useEffect(() => {
        if (qmr && qmr.assets && qmr.assets.length > 0) {
            const pending = qmr.assets.filter((asset, index) => asset.media.length == 0);
            if (pending.length > 0) {
                setTimeout(() => {
                    fetchQmrDetail(false);
                }, 1000);
            }
        }
    }, [qmr]);

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        if (!qmr) {
            return;
        }

        async function checkForMatchingFailCode() {
            if (!qmr) {
                return;
            }

            try {
                const response = await failCodesService.lookupFailCodes({
                    qmrId: qmr.qmrId,
                    partNumber: qmr.partNumber,
                    ignoreCache: true,
                });

                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                if (!!response.data.failCodes.length) {
                    setMatchingFailCode(response.data.failCodes[0]);
                } else {
                    setMatchingFailCode(null);
                }
            } catch (error) {
                // Fail silently and remove any matching fail code.
                setMatchingFailCode(null);
            }
        }

        checkForMatchingFailCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qmr?.partNumber]);

    useEffect(() => {
        if (location.state) {
            setQmrTypeChangedMsg(location.state.message);
        }
    }, [location.state]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setQmrTypeChangedMsg('');
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    if (!qmr && isFetching) {
        return <Loader />;
    }

    if (qmrFetchRestricted) {
        return <QmrNotFound />;
    }

    if (!qmr) {
        return <NoMatch />;
    }

    if (!!editRouteMatch && !qmr.capabilities.editQmr) {
        return <Navigate to={`/qmrs/${qmr.displayIdentifier}`} />;
    } else if (!editRouteMatch && !qmr.capabilities.viewQmr) {
        return <QmrNotFound />;
    }

    const divider = <hr className={classes.sectionDivider} />;

    return (
        <>
            <ToastNotification
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name="checkmark" size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {toast.message}
                        </Typography>
                    </div>
                }
                onClose={handleCloseToast}
                show={toast.show}
            />
            <AlertBar
                success
                show={!!qmrTypeChangedMsg.length}
                onClose={() => {
                    setQmrTypeChangedMsg('');
                }}
                style={{ zIndex: 1 }}
            >
                <Typography variant="h5">{qmrTypeChangedMsg}</Typography>
            </AlertBar>

            {isQmrFeedbackDrawerVisible && <QmrFeedbackDrawer />}

            <QmrLayout checklistContainerId={CHECKLIST_CONTAINER_ID} checklistSectionIds={CHECKLIST_SECTION_IDS}>
                <AlertBar
                    success
                    show={restrictedRecentlyState.restricted || restrictedRecentlyState.unrestricted}
                    onClose={() => {
                        setRestrictedRecentlyState({
                            restricted: false,
                            unrestricted: false,
                        });
                    }}
                    style={{ zIndex: 1 }}
                >
                    <Typography variant="h5">
                        {restrictedRecentlyState.restricted
                            ? t('alerts:qmrResitrcted.message', 'QMR has been marked as Restricted')
                            : restrictedRecentlyState.restricted
                              ? t('alerts:qmrUnresitrcted.message', 'QMR has been marked as Unrestricted')
                              : 'The report has been successfully updated and changed to QMR.'}
                    </Typography>
                </AlertBar>
                <AlertBar
                    success
                    show={showRecentlyCreatedMessage}
                    onClose={() => {
                        setShowRecentlyCreatedMessage(false);
                        navigate(location.pathname, {
                            state: {
                                isRecent: false,
                            },
                        });
                    }}
                >
                    <Typography variant="h5">
                        {t('alerts:qmrCreated.message', 'QMR has been created for {{year}} {{model}} VIN: {{vin}}', {
                            year: qmr.modelYear,
                            model: qmr.carlineName,
                            vin: qmr.vin,
                        })}
                    </Typography>
                </AlertBar>

                <AlertBar
                    error
                    show={showQmrEscalatedFailedMessage}
                    onClose={() => {
                        setShowQmrEscalatedFailedMessage(false);
                    }}
                >
                    <Typography variant="h5">
                        {t(
                            'alerts:qmrEscalatedFailed.message',
                            'This QMR failed to escalate to the Technical Reporting System (TR System).'
                        )}
                    </Typography>
                </AlertBar>
                <AlertBar
                    warning
                    show={showQmrEscalatedTrMessage}
                    onClose={() => {
                        setShowQmrEscalatedTrMessage(false);
                    }}
                >
                    <Typography variant="h5">
                        {t(
                            'alerts:qmrEscalatedTr.message',
                            'This QMR was escalated to the Technical Reporting System (TR System). Any changes to this QMR will not be reflected in the TR.'
                        )}
                    </Typography>
                </AlertBar>

                {isEditing ? (
                    <>
                        <section id={sectionIdsMap.vehicle} className={classNames('pt-6', classes.section)}>
                            <VehicleInformationDraft />
                        </section>

                        {isPrimaryObjectiveEnabled && qmr.capabilities.viewCatOnQmr && (
                            <>
                                {divider}
                                <section
                                    id={sectionIdsMap.primaryObjective}
                                    className={classNames('pt-6', classes.section)}
                                >
                                    <PrimaryObjective viewOnly={!qmr.capabilities.editCatOnQmr} />
                                </section>
                            </>
                        )}

                        {divider}

                        <section id={sectionIdsMap.concern} className={classes.section}>
                            <ConcernDraft
                                showConvertedToQmrMessage={() => {
                                    setQmrTypeChangedMsg(
                                        'The report has been successfully updated and changed to QMR.'
                                    );
                                }}
                            />
                        </section>

                        {divider}

                        <section id={sectionIdsMap.cause} className={classes.section}>
                            <CauseDraft />
                        </section>

                        {divider}

                        <section id={sectionIdsMap.correction} className={classes.section}>
                            <CorrectionDraft />
                        </section>

                        {divider}

                        <section id={sectionIdsMap.dtc} className={classes.section}>
                            <DtcCodesDraft />
                        </section>
                        {divider}
                    </>
                ) : (
                    <>
                        <section id={sectionIdsMap.vehicle} className={classNames('pt-6', classes.section)}>
                            <VehicleInformationSubmitted />
                        </section>

                        {isPrimaryObjectiveEnabled && qmr.capabilities.viewCatOnQmr && (
                            <>
                                {divider}
                                <section
                                    id={sectionIdsMap.primaryObjective}
                                    className={classNames('pt-6', classes.section)}
                                >
                                    <PrimaryObjective viewOnly={true} />
                                </section>
                            </>
                        )}

                        {divider}

                        {qmr.capabilities.viewCccOnQmr && (
                            <>
                                <section id={sectionIdsMap.concern} className={classes.section}>
                                    <ConcernSubmitted />
                                </section>

                                {divider}

                                <section id={sectionIdsMap.cause} className={classes.section}>
                                    <CauseSubmitted />
                                </section>

                                {divider}

                                <section id={sectionIdsMap.correction} className={classes.section}>
                                    <CorrectionSubmitted />
                                </section>
                            </>
                        )}

                        {divider}

                        {qmr.capabilities.viewDtcOnQmr && (
                            <section id={sectionIdsMap.dtc} className={classes.section}>
                                <DtcCodesSubmitted />
                            </section>
                        )}
                        {divider}
                    </>
                )}

                {(isDraft || qmr.capabilities.viewFailCodeOnQmr) && (
                    <>
                        <section id={sectionIdsMap.failCodes} className={classes.section}>
                            <FailCodeReportSection
                                onShowToast={handleShowToast}
                                potentialFailCodeFound={!!matchingFailCode}
                            />
                        </section>

                        {divider}
                    </>
                )}

                {qmr.capabilities.viewMediaOnQmr && (
                    <>
                        <section id={sectionIdsMap.attachments} className={classes.section}>
                            <Attachments toggle={() => setToggle(true)} />
                        </section>

                        {divider}
                    </>
                )}

                {qmr.outcomeAvailable && (
                    <>
                        <section id={sectionIdsMap.qmrOutcome} className={classes.section}>
                            <QmrOutcome />
                        </section>

                        {divider}
                    </>
                )}

                <section className={classNames(classes.section, 'mt-6')}>
                    <AlertBar show={hasOpenInfoRequest} error onClose={() => {}}>
                        <Typography>More information has been requested.</Typography>
                    </AlertBar>
                </section>

                {qmr.qmrStatus.qmrStatusId !== QmrStatusId.Draft && (
                    <section
                        id={sectionIdsMap.additionalInfo}
                        className={classNames(classes.section, classes.infoSection, 'mt-6')}
                    >
                        <AdditionalInformation />
                    </section>
                )}
            </QmrLayout>
        </>
    );
};

export default QmrDetail;
